import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, CalendarPlus, Check, GitCompare, Share2, Star, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr } from "@/lib/api";
import { fromISTInput, inrCompact } from "@/lib/format";
import { EmptyState, Spinner, TempBadge } from "@/components/common";
import DatePicker from "@/components/DatePicker";
import { shareProperty } from "@/components/properties/share";
import { cn } from "@/lib/utils";

export default function MatchList({ params, testid = "match-list" }) {
  const qc = useQueryClient();
  const [visit, setVisit] = useState(null);
  const [at, setAt] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["matches", params], queryFn: async () => (await api.get("/matches", { params })).data });
  const act = async (m, action) => {
    try {
      await api.post("/matches/action", { lead_id: m.lead.id, property_id: m.property.id, action });
      toast.success(action === "dismiss" ? "Match dismissed" : "Updated");
      qc.invalidateQueries({ queryKey: ["matches"] });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const book = async () => {
    try {
      await api.post("/matches/site-visit", { lead_id: visit.lead.id, property_id: visit.property.id, action: "shortlist", at: fromISTInput(at) });
      toast.success("Site visit scheduled");
      setVisit(null);
      qc.invalidateQueries();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  if (isLoading) return <Spinner />;
  if (!data.length) return <EmptyState icon={GitCompare} title="No matches yet" text="Matches appear when an active lead fits an available property." testid={`${testid}-empty`} />;
  return (
    <div className="space-y-3" data-testid={testid}>
      {data.map((m) => (
        <div key={`${m.lead.id}-${m.property.id}`} data-testid={`match-${m.lead.id}-${m.property.id}`} className="rounded-2xl border bg-card p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className={cn("num flex h-12 w-12 items-center justify-center rounded-full text-sm font-bold text-white", m.score >= 70 ? "bg-emerald-600" : m.score >= 50 ? "bg-amber-500" : "bg-slate-400")} data-testid="match-score">{m.score}%</span>
            <div className="min-w-0 flex-1 text-sm">
              <Link to={`/leads/${m.lead.id}`} className="font-semibold hover:underline">{m.lead.name}</Link> <TempBadge t={m.lead.temperature} />
              <p className="text-muted-foreground">↔ <Link to={`/properties/${m.property.id}`} className="hover:underline">{m.property.property_type}{m.property.bhk ? ` · ${m.property.bhk}` : ""} · {m.property.project}</Link> · {inrCompact(m.property.price)}</p>
            </div>
            {m.status === "shortlisted" && <span className="flex items-center gap-1 text-xs font-semibold text-amber-600"><Star className="h-3.5 w-3.5" />Shortlisted</span>}
          </div>
          <div className="mt-3 grid gap-1 sm:grid-cols-2">
            {m.criteria.map((c) => (
              <p key={c.name} className="flex items-start gap-1.5 text-xs">
                {c.matched ? <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> : <X className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />}
                <span><span className="font-semibold">{c.name}</span> <span className="text-muted-foreground">({c.weight}) {c.why}</span></span>
              </p>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => act(m, m.status === "shortlisted" ? "unshortlist" : "shortlist")} data-testid="match-shortlist"><Star className="mr-1 h-3.5 w-3.5" />{m.status === "shortlisted" ? "Unshortlist" : "Shortlist"}</Button>
            <Button size="sm" variant="outline" onClick={async () => shareProperty((await api.get(`/properties/${m.property.id}`)).data)} data-testid="match-share"><Share2 className="mr-1 h-3.5 w-3.5" />WhatsApp</Button>
            <Button size="sm" variant="outline" onClick={() => { setVisit(m); setAt(""); }} data-testid="match-visit"><CalendarPlus className="mr-1 h-3.5 w-3.5" />Site visit</Button>
            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(m, "dismiss")} data-testid="match-dismiss"><Ban className="mr-1 h-3.5 w-3.5" />Dismiss</Button>
          </div>
        </div>
      ))}
      <Dialog open={!!visit} onOpenChange={(o) => !o && setVisit(null)}>
        <DialogContent data-testid="site-visit-dialog">
          <DialogHeader><DialogTitle>Schedule site visit</DialogTitle></DialogHeader>
          <DatePicker withTime testid="site-visit-at" value={at} onChange={setAt} />
          <DialogFooter><Button disabled={!at} onClick={book} data-testid="site-visit-save">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
